import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Loader2, AlertTriangle, Rocket } from "lucide-react";
import StylePicker from "../components/video/StylePicker";
import NarratorField from "../components/video/NarratorField";
import SoundtrackField from "../components/video/SoundtrackField";
import { nicheByKey } from "@/lib/niches";

const NICHES = ["Finance & Money", "Tech & AI", "History", "Motivation", "True Crime", "Health & Fitness", "Science", "Business", "Gaming", "General"];
const TONES = ["Energetic & engaging", "Calm & documentary", "Bold & provocative", "Educational & clear", "Storytelling & cinematic"];
const PLATFORMS = ["YouTube", "TikTok", "Instagram"];
const LENGTHS = ["Long (1-3 min)", "Short (30-60s)"];

// Keep in sync with backend core.py: COST_SCRIPT=10, COST_IMAGE=5, voiceover OpenAI=5/ElevenLabs=10; scenes = Long 7 / Short 3.
const COST_SCRIPT = 10;
const COST_IMAGE = 5;
const COST_VO_OPENAI = 5;
const COST_VO_ELEVENLABS = 10;

export default function Create() {
  const [form, setForm] = useState({
    topic: "",
    niche: "Finance & Money",
    tone: "Energetic & engaging",
    platform: "YouTube",
    length: "Long (1-3 min)",
    voice_id: "",
    visual_style: "cinematic",
    music_track: "",
    music_volume: "subtle",
  });
  const [loading, setLoading] = useState(false);
  const [genStep, setGenStep] = useState("");
  const [action, setAction] = useState(null);
  const [voices, setVoices] = useState([]);
  const { user, updateCredits } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Cost estimate (script + auto visuals). Long = 7 scenes, Short = 3.
  const numScenes = form.length.includes("Short") ? 3 : 7;
  const visualsCost = numScenes * COST_IMAGE;
  const totalCost = COST_SCRIPT + visualsCost;
  const voCost = form.voice_id?.startsWith("openai:") ? COST_VO_OPENAI : COST_VO_ELEVENLABS;
  const publishTotal = totalCost + voCost;
  const credits = user?.credits ?? 0;
  const cantAffordScript = credits < COST_SCRIPT;
  const insufficient = credits < totalCost;
  const canPublish = credits >= publishTotal;

  useEffect(() => {
    const picked = nicheByKey((searchParams.get("niche") || "").trim());
    if (picked) setForm((f) => ({ ...f, niche: picked.niche, topic: f.topic || picked.topic }));
    api
      .get("/voices")
      .then((r) => {
        const list = r.data.voices || [];
        setVoices(list);
        setForm((f) => ({ ...f, voice_id: f.voice_id || list[0]?.voice_id || "" }));
      })
      .catch(() => {});
  }, []);

  const generate = async (publish = false) => {
    if (!form.topic.trim()) {
      toast.error("Enter a topic or idea first");
      return;
    }
    if (cantAffordScript) {
      toast.error("Please refill credits to complete");
      return;
    }
    if (publish && !canPublish) {
      toast.error("Please refill credits to publish");
      return;
    }
    setLoading(true);
    setAction(publish ? "publish" : "create");
    setGenStep("Cooking up your script…");
    try {
      const { data } = await api.post("/videos/generate-script", { ...form, publish });
      updateCredits(data.credits);
      const videoId = data.video_id;
      const start = Date.now();
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/videos/${videoId}/generation-status`);
          if (typeof st.credits === "number") updateCredits(st.credits);
          if (st.gen_status === "ready") {
            toast.success(publish ? "Your video is published!" : "Your video is ready!");
            navigate(`/video/${videoId}`);
            return;
          }
          if (st.gen_status === "error") {
            toast.error(st.gen_error || "Generation failed — please try again.");
            setLoading(false);
            setGenStep("");
            setAction(null);
            return;
          }
          setGenStep(st.gen_step || "Working…");
          if (Date.now() - start > 5 * 60 * 1000) {
            toast.message("Still working — find it in your Library shortly.");
            navigate("/library");
            return;
          }
          setTimeout(poll, 2500);
        } catch (e) {
          // transient network/poll error — keep polling
          if (Date.now() - start < 5 * 60 * 1000) setTimeout(poll, 3000);
          else { setLoading(false); setGenStep(""); setAction(null); }
        }
      };
      setTimeout(poll, 2500);
    } catch (err) {
      toast.error(apiError(err));
      setLoading(false);
      setGenStep("");
      setAction(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="create-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">AI Video Creator</p>
        <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight">What should we forge?</h1>
        <p className="text-zinc-400 mt-2">Describe your idea — we'll write a retention-optimized script and auto-generate scene visuals, then add your voiceover in the editor.</p>
      </div>

      <div className="border border-white/10 rounded-md bg-[#121212] p-6 md:p-8 space-y-6">
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Topic / idea</label>
          <textarea
            value={form.topic}
            onChange={(e) => set("topic")(e.target.value)}
            data-testid="create-topic"
            rows={3}
            placeholder="e.g. 5 money habits that quietly make people rich"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SelectField label="Niche" value={form.niche} options={NICHES} onChange={set("niche")} testid="create-niche" />
          <SelectField label="Tone" value={form.tone} options={TONES} onChange={set("tone")} testid="create-tone" />
          <SelectField label="Platform" value={form.platform} options={PLATFORMS} onChange={set("platform")} testid="create-platform" />
          <SelectField label="Length" value={form.length} options={LENGTHS} onChange={set("length")} testid="create-length" />
        </div>

        <NarratorField voices={voices} value={form.voice_id} onChange={set("voice_id")} />

        <SoundtrackField
          track={form.music_track}
          volume={form.music_volume}
          voiceId={form.voice_id}
          onChange={(v) => setForm((f) => ({ ...f, music_track: v.track || "", music_volume: v.volume }))}
        />

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Visual style</label>
          <StylePicker value={form.visual_style} onChange={(v) => set("visual_style")(v)} testid="create-visual-style" />
          <p className="text-xs text-zinc-600 mt-1.5">Sets the look of your AI scene images — applied to every scene for a consistent feel.</p>
        </div>

        <div className="border border-white/10 rounded-sm bg-[#0A0A0A] px-4 py-3" data-testid="create-cost-summary">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-400">Script <span className="text-zinc-600">({COST_SCRIPT})</span> + Visuals <span className="text-zinc-600">({visualsCost} · {numScenes} scenes)</span></span>
            <span className="font-semibold text-white" data-testid="create-cost-total">{totalCost} credits</span>
          </div>
          <div className="flex items-center justify-between text-xs mt-1.5 text-zinc-500">
            <span>Your balance</span>
            <span data-testid="create-credit-balance">{credits} credits</span>
          </div>
          {insufficient && (
            <div className="mt-3 flex items-start gap-2 rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="create-refill-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Please refill credits to complete.{" "}
                {cantAffordScript
                  ? "You don't have enough credits to generate a script."
                  : "Visuals will be skipped — you can add them manually in the editor."}{" "}
                <Link to="/credits" className="underline font-semibold hover:text-amber-200" data-testid="create-refill-link">Buy credits</Link>
              </span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <button
            onClick={() => generate(false)}
            disabled={loading || cantAffordScript}
            data-testid="generate-script-btn"
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,122,255,0.3)]"
          >
            {loading
              ? (action === "create"
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> {genStep || "Creating your video…"}</>
                  : <>Please wait…</>)
              : <><Sparkles className="h-4 w-4" /> Create now</>}
          </button>

          <button
            onClick={() => generate(true)}
            disabled={loading || !canPublish}
            data-testid="create-publish-btn"
            title={canPublish ? "Script + visuals + voiceover, rendered & published in one tap" : `Needs ${publishTotal} credits to publish`}
            className="w-full bg-white/5 border border-white/15 hover:bg-white/10 hover:border-white/25 disabled:opacity-50 disabled:cursor-not-allowed py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-all duration-200"
          >
            {loading && action === "publish"
              ? <><Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> {genStep || "Publishing…"}</>
              : loading
                ? <>Please wait…</>
                : <><Rocket className="h-4 w-4 text-emerald-400" /> Create &amp; Publish <span className="text-xs text-zinc-500 font-normal">· {publishTotal} credits</span></>}
          </button>
          <p className="text-xs text-zinc-600 text-center" data-testid="create-publish-note">
            {canPublish
              ? "One tap: writes the script, generates visuals, records the voiceover, renders the MP4 and publishes it — ready to download or schedule."
              : `Add ${publishTotal - credits} more credits to use one-tap publish (renders a finished MP4).`}
          </p>
        </div>

        <p className="text-xs text-zinc-600 text-center" data-testid="create-copyright-notice">
          By generating, you confirm your topic and resulting content won't infringe any copyright or trademark.{" "}
          <Link to="/legal" className="text-zinc-500 hover:text-zinc-300 underline">Content policy</Link>
        </p>
      </div>
    </div>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
